import React, { useState, useEffect } from 'react';
import { useLogo } from '../contexts/LogoContext';
import Logo from './Logo';

const AdminDashboard = ({ onLogout }) => {
  const { refreshLogo } = useLogo();
  const [stats, setStats] = useState({
    totalChats: 0,
    crisisFlags: 0,
    documentsCount: 0,
    lastIndexed: null
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentsMessage, setDocumentsMessage] = useState('');
  const [pendingDocuments, setPendingDocuments] = useState([]);
  const [reindexing, setReindexing] = useState(false);
  const [crisisKeywords, setCrisisKeywords] = useState({
    crisis_line_cue_words: []
  });
  const [showCrisisEditor, setShowCrisisEditor] = useState(false);
  const [newKeyword, setNewKeyword] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('crisis_line_cue_words');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [convertedFiles, setConvertedFiles] = useState([]);
  const [converting, setConverting] = useState(false);
  const [indexing, setIndexing] = useState(false);
  const [crisisTriggers, setCrisisTriggers] = useState([]);
  const [showTriggers, setShowTriggers] = useState(false);
  
  // Export filters
  const [exportStartDate, setExportStartDate] = useState('');
  const [exportEndDate, setExportEndDate] = useState('');
  const [exportOnlyFlagged, setExportOnlyFlagged] = useState(false);
  const [showExportFilters, setShowExportFilters] = useState(false);

  // Analytics state
  const [analytics, setAnalytics] = useState({
    today: { chats: 0, crisis: 0, date: '' },
    topKeywords: [],
    topRagSources: [],
    hourlyDistribution: {},
    weeklyTrend: []
  });
  const [showAnalytics, setShowAnalytics] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchPendingDocuments();
    fetchCrisisKeywords();
    fetchRawFiles();
    fetchProcessedFiles();
    fetchCrisisTriggers();
    fetchAnalytics();
  }, []);

  // Convert UTC time to PST
  const toPSTTime = (utcTime) => {
    if (!utcTime) return 'Never';
    const date = new Date(utcTime);
    return date.toLocaleString('en-US', {
      timeZone: 'America/Los_Angeles',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short'
    });
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/admin/stats', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const fetchPendingDocuments = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/admin/pending-files', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPendingDocuments(data.files || []);
      }
    } catch (err) {
      console.error('Failed to fetch pending documents:', err);
    }
  };

  const fetchCrisisKeywords = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/admin/crisis-keywords', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCrisisKeywords(data.keywords);
      }
    } catch (err) {
      console.error('Failed to fetch crisis keywords:', err);
    }
  };

  const fetchRawFiles = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/admin/pending-files', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUploadedFiles(data.files || []);
      }
    } catch (err) {
      console.error('Failed to fetch pending files:', err);
    }
  };

  const fetchProcessedFiles = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/admin/processed-files', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setConvertedFiles(data.files || []);
      }
    } catch (err) {
      console.error('Failed to fetch processed files:', err);
    }
  };

  const fetchCrisisTriggers = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/admin/crisis-triggers', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCrisisTriggers(data.crisis_triggers || []);
      }
    } catch (err) {
      console.error('Failed to fetch crisis triggers:', err);
    }
  };

  const markTriggerReviewed = async (triggerId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/admin/mark-trigger-reviewed/${triggerId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchCrisisTriggers(); // Refresh the list
      }
    } catch (err) {
      console.error('Failed to mark trigger as reviewed:', err);
    }
  };

  const markAllTriggersReviewed = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/admin/mark-all-triggers-reviewed', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        alert(data.message);
        fetchCrisisTriggers(); // Refresh the list
      } else {
        alert('Failed to mark all triggers as reviewed');
      }
    } catch (err) {
      console.error('Failed to mark all triggers as reviewed:', err);
      alert('Error marking all triggers as reviewed');
    }
  };

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/admin/dashboard-analytics', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Please select a valid image file (PNG, JPG, GIF, WebP)');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    setLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/admin/upload-logo', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        alert('Logo uploaded successfully!');
        // Force refresh logo with delay to ensure file is written
        setTimeout(() => {
          refreshLogo();
        }, 500);
        fetchStats();
      } else {
        const error = await response.text();
        alert('Failed to upload logo: ' + error);
      }
    } catch (error) {
      console.error('Logo upload error:', error);
      alert('Error uploading logo');
    } finally {
      setLoading(false);
      e.target.value = '';
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setDocumentsLoading(true);
    setDocumentsMessage('');
    
    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/admin/upload-file', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setDocumentsMessage(`✅ ${data.message}`);
        fetchRawFiles();
        fetchStats();
      } else {
        const error = await response.text();
        setDocumentsMessage(`❌ Upload failed: ${error}`);
      }
    } catch (error) {
      console.error('File upload error:', error);
      setDocumentsMessage('❌ Upload failed: ' + error.message);
    } finally {
      setDocumentsLoading(false);
      e.target.value = '';
    }
  };

  const handleConvertToJson = async () => {
    setConverting(true);
    setDocumentsMessage('');
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/admin/convert-pending-files', {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setDocumentsMessage(`✅ ${data.message}`);
        fetchRawFiles();
        fetchProcessedFiles();
        fetchStats();
      } else {
        const error = await response.text();
        setDocumentsMessage(`❌ Conversion failed: ${error}`);
      }
    } catch (error) {
      console.error('Conversion error:', error);
      setDocumentsMessage('❌ Conversion failed: ' + error.message);
    } finally {
      setConverting(false);
    }
  };

  const handleStartReindex = async (jsonFilename) => {
    setIndexing(true);
    setDocumentsMessage('');
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/admin/start-reindex', {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ json_filename: jsonFilename })
      });

      if (response.ok) {
        const data = await response.json();
        setDocumentsMessage(`✅ Reindexing complete: ${data.chunks_processed} chunks processed`);
        fetchConvertedFiles();
        fetchPendingDocuments();
        fetchStats();
      } else {
        const error = await response.text();
        setDocumentsMessage(`❌ Reindexing failed: ${error}`);
      }
    } catch (error) {
      console.error('Reindexing error:', error);
      setDocumentsMessage('❌ Reindexing failed: ' + error.message);
    } finally {
      setIndexing(false);
    }
  };

  const handleReindex = async () => {
    setReindexing(true);
    setDocumentsMessage('');
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/admin/reindex-vector-database', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setDocumentsMessage(`✅ ${data.message}`);
        fetchProcessedFiles();
        fetchStats();
      } else {
        const error = await response.text();
        setDocumentsMessage(`❌ Reindexing failed: ${error}`);
      }
    } catch (error) {
      console.error('Reindexing error:', error);
      setDocumentsMessage('❌ Reindexing failed: ' + error.message);
    } finally {
      setReindexing(false);
    }
  };

  const handleExportDB = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/admin/export-db', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `database_backup_${new Date().toISOString().split('T')[0]}.db`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        alert('Failed to export database');
      }
    } catch (error) {
      console.error('Export error:', error);
      alert('Error exporting database');
    }
  };

  const handleExportChats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/admin/export-chats', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `chat_logs_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        alert('Failed to export chat logs');
      }
    } catch (error) {
      console.error('Export error:', error);
      alert('Error exporting chat logs');
    }
  };

  const handleExportChatsFiltered = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      
      const filterData = {
        start_date: exportStartDate || undefined,
        end_date: exportEndDate || undefined,
        only_flagged: exportOnlyFlagged
      };
      
      const response = await fetch('/api/admin/export-chats-filtered', {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(filterData)
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        
        // Create descriptive filename
        let filename = `chat_logs_filtered_${new Date().toISOString().split('T')[0]}`;
        if (exportStartDate) filename += `_from_${exportStartDate}`;
        if (exportEndDate) filename += `_to_${exportEndDate}`;
        if (exportOnlyFlagged) filename += `_flagged_only`;
        filename += '.csv';
        
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        
        alert('✅ Filtered chat logs exported successfully');
      } else {
        const error = await response.text();
        alert('❌ Filtered export failed: ' + error);
      }
    } catch (error) {
      console.error('Filtered export error:', error);
      alert('❌ Filtered export error occurred');
    }
  };

  const handleClearChats = async () => {
    if (window.confirm('⚠️ Are you sure you want to clear ALL chat history? This cannot be undone!')) {
      try {
        const token = localStorage.getItem('adminToken');
        const response = await fetch('/api/admin/clear-chats', {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          alert('✅ All chat history cleared successfully');
          fetchStats();
        } else {
          alert('❌ Failed to clear chat history');
        }
      } catch (error) {
        console.error('Clear chats error:', error);
        alert('Error clearing chat history');
      }
    }
  };

  const addCrisisKeyword = () => {
    if (!newKeyword.trim()) return;
    
    const keyword = newKeyword.trim();
    if (crisisKeywords.crisis_line_cue_words.includes(keyword)) {
      alert('Keyword already exists');
      return;
    }

    const updatedKeywords = {
      ...crisisKeywords,
      crisis_line_cue_words: [...crisisKeywords.crisis_line_cue_words, keyword]
    };
    
    setCrisisKeywords(updatedKeywords);
    setNewKeyword('');
  };

  const removeCrisisKeyword = (keyword) => {
    if (window.confirm(`Remove "${keyword}" from crisis line cue words?`)) {
      const updatedKeywords = {
        ...crisisKeywords,
        crisis_line_cue_words: crisisKeywords.crisis_line_cue_words.filter(k => k !== keyword)
      };
      setCrisisKeywords(updatedKeywords);
    }
  };

  const saveCrisisKeywords = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/admin/crisis-keywords', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(crisisKeywords)
      });

      if (response.ok) {
        alert('✅ Crisis keywords updated successfully!');
        setShowCrisisEditor(false);
      } else {
        const error = await response.text();
        alert('❌ Failed to update keywords: ' + error);
      }
    } catch (error) {
      console.error('Save keywords error:', error);
      alert('Error saving keywords');
    }
  };

  const renderChart = (data, title, type = 'bar') => {
    if (!data || data.length === 0) {
      return <div style={{ color: '#666', fontSize: '0.9rem', textAlign: 'center' }}>No data available</div>;
    }

    const maxValue = Math.max(...data.map(item => item.count || item.chats || item.crisis || 0));
    
    return (
      <div>
        <h4 style={{ margin: '0 0 1rem 0', color: 'var(--color-dark-green)', fontSize: '1rem' }}>{title}</h4>
        {type === 'bar' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {data.slice(0, 8).map((item, index) => (
              <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <div style={{ 
                  width: '100px', 
                  fontSize: '0.8rem', 
                  textAlign: 'right',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {item.keyword || item.source || item.date}
                </div>
                <div style={{ 
                  flex: 1, 
                  height: '20px', 
                  background: '#f0f0f0', 
                  borderRadius: '10px',
                  position: 'relative'
                }}>
                  <div style={{
                    height: '100%',
                    width: `${maxValue > 0 ? ((item.count || item.chats || item.crisis || 0) / maxValue) * 100 : 0}%`,
                    background: 'linear-gradient(90deg, var(--color-light-green), var(--color-dark-green))',
                    borderRadius: '10px',
                    transition: 'width 0.3s ease'
                  }} />
                </div>
                <div style={{ fontSize: '0.8rem', fontWeight: 'bold', width: '30px', textAlign: 'center' }}>
                  {item.count || item.chats || item.crisis || 0}
                </div>
              </div>
            ))}
          </div>
        )}
        {type === 'line' && (
          <div style={{ display: 'flex', alignItems: 'end', gap: '2px', height: '100px' }}>
            {data.map((item, index) => (
              <div key={index} style={{ 
                flex: 1, 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center',
                gap: '0.25rem'
              }}>
                <div style={{
                  height: `${maxValue > 0 ? ((item.chats || 0) / maxValue) * 80 : 2}px`,
                  width: '100%',
                  background: 'var(--color-blue)',
                  borderRadius: '2px 2px 0 0',
                  minHeight: '2px'
                }} />
                <div style={{ fontSize: '0.7rem', transform: 'rotate(-45deg)', transformOrigin: 'center' }}>
                  {item.date?.split('-')[2] || index}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      padding: '2rem'
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem',
          background: 'white',
          padding: '1.5rem',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
        }}>
          <div>
            <h1 style={{
              margin: '0 0 0.5rem 0',
              color: 'var(--color-dark-green)',
              fontSize: '1.8rem'
            }}>
              🔐 S.U.E. Admin Dashboard
            </h1>
            <p style={{ color: '#666', margin: 0 }}>
              System management and analytics - Last updated: {toPSTTime(new Date())}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <button
              onClick={() => window.location.href = '/'}
              style={{
                padding: '0.75rem 1.5rem',
                background: 'var(--color-light-green)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              ← Back to S.U.E. Chat
            </button>
            <button
              onClick={onLogout}
              style={{
                padding: '0.75rem 1.5rem',
                background: 'var(--color-red)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Logout
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '1.5rem',
          marginBottom: '2rem'
        }}>
          <div style={{
            background: 'white',
            padding: '1.5rem',
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>💬</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--color-dark-green)' }}>
              {stats.totalChats}
            </div>
            <div style={{ color: '#666', fontSize: '0.9rem' }}>Total Chats</div>
          </div>

          <div style={{
            background: 'white',
            padding: '1.5rem',
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🚨</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--color-red)' }}>
              {stats.crisisFlags}
            </div>
            <div style={{ color: '#666', fontSize: '0.9rem' }}>Crisis Flags</div>
          </div>

          <div style={{
            background: 'white',
            padding: '1.5rem',
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📚</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--color-purple)' }}>
              {stats.documentsCount}
            </div>
            <div style={{ color: '#666', fontSize: '0.9rem' }}>Documents</div>
          </div>

          <div style={{
            background: 'white',
            padding: '1.5rem',
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🔄</div>
            <div style={{ fontSize: '1rem', fontWeight: 'bold', color: 'var(--color-dark-green)' }}>
              {toPSTTime(stats.lastIndexed)}
            </div>
            <div style={{ color: '#666', fontSize: '0.9rem' }}>Last Indexed (PST)</div>
          </div>
        </div>

        {/* Analytics Dashboard */}
        <div style={{
          background: 'white',
          padding: '1.5rem',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          marginBottom: '2rem'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h3 style={{ margin: 0, color: 'var(--color-dark-green)' }}>📊 Analytics Dashboard</h3>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <button
                onClick={fetchAnalytics}
                style={{
                  padding: '0.5rem 1rem',
                  background: 'var(--color-blue)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.8rem'
                }}
              >
                🔄 Refresh
              </button>
              <button
                onClick={() => setShowAnalytics(!showAnalytics)}
                style={{
                  padding: '0.5rem 1rem',
                  background: showAnalytics ? 'var(--color-red)' : 'var(--color-dark-green)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.8rem'
                }}
              >
                {showAnalytics ? 'Hide Dashboard' : 'Show Dashboard'}
              </button>
            </div>
          </div>

          {showAnalytics && (
            <div>
              {/* Today's Summary */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: '1rem',
                marginBottom: '2rem',
                padding: '1rem',
                background: '#f8f9fa',
                borderRadius: '8px'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--color-blue)' }}>
                    {analytics.today.chats}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#666' }}>Today's Chats</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--color-red)' }}>
                    {analytics.today.crisis}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#666' }}>Today's Crisis</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1rem', fontWeight: 'bold', color: 'var(--color-purple)' }}>
                    {analytics.today.date}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#666' }}>Date (PST)</div>
                </div>
              </div>

              {/* Charts Grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '1.5rem'
              }}>
                {/* Top Keywords */}
                <div style={{
                  padding: '1rem',
                  background: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #e9ecef'
                }}>
                  {renderChart(analytics.topKeywords, '🔥 Today\'s Top Keywords')}
                </div>

                {/* Top RAG Sources */}
                <div style={{
                  padding: '1rem',
                  background: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #e9ecef'
                }}>
                  {renderChart(analytics.topRagSources, '📚 Most Referenced Sources')}
                </div>

                {/* Weekly Trend */}
                <div style={{
                  padding: '1rem',
                  background: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #e9ecef',
                  gridColumn: 'span 2'
                }}>
                  {renderChart(analytics.weeklyTrend, '📈 7-Day Trend', 'line')}
                </div>
              </div>

              {/* Hourly Distribution */}
              <div style={{
                marginTop: '1.5rem',
                padding: '1rem',
                background: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #e9ecef'
              }}>
                <h4 style={{ margin: '0 0 1rem 0', color: 'var(--color-dark-green)' }}>🕒 Today's Hourly Activity</h4>
                <div style={{ display: 'flex', alignItems: 'end', gap: '1px', height: '60px', overflow: 'auto' }}>
                  {Object.entries(analytics.hourlyDistribution).map(([hour, count]) => (
                    <div key={hour} style={{ 
                      flex: '1',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}>
                      <div style={{
                        height: `${Math.max(count * 3, 2)}px`,
                        width: '100%',
                        background: count > 0 ? 'var(--color-light-green)' : '#e9ecef',
                        borderRadius: '2px 2px 0 0',
                        minHeight: '2px'
                      }} />
                      <div style={{ fontSize: '0.6rem', writingMode: 'vertical-lr' }}>
                        {hour}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Admin Panels */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
          gap: '1.5rem'
        }}>
          
          {/* Export Panel */}
          <div className="admin-panel" style={{
            background: 'white',
            padding: '1.5rem',
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
          }}>
            <h3>📊 Data Export</h3>
            <p>Export system data for backup or analysis</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <button
                onClick={handleExportDB}
                style={{
                  padding: '0.75rem',
                  background: 'var(--color-dark-green)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Export Database (.db)
              </button>
              <button
                onClick={handleExportChats}
                style={{
                  padding: '0.75rem',
                  background: 'var(--color-purple)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Export All Chat Logs (CSV)
              </button>
              
              {/* Filtered Export Section */}
              <div style={{ 
                marginTop: '1rem', 
                padding: '1rem', 
                background: '#f8f9fa', 
                borderRadius: '8px',
                border: '1px solid #e9ecef'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h4 style={{ margin: 0, color: 'var(--color-blue)' }}>🔍 Filtered Export</h4>
                  <button
                    onClick={() => setShowExportFilters(!showExportFilters)}
                    style={{
                      padding: '0.5rem 1rem',
                      background: showExportFilters ? 'var(--color-red)' : 'var(--color-blue)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.8rem'
                    }}
                  >
                    {showExportFilters ? 'Hide Filters' : 'Show Filters'}
                  </button>
                </div>
                
                {showExportFilters && (
                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '1rem' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>
                          Start Date (PST):
                        </label>
                        <input
                          type="date"
                          value={exportStartDate}
                          onChange={(e) => setExportStartDate(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '0.8rem'
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>
                          End Date (PST):
                        </label>
                        <input
                          type="date"
                          value={exportEndDate}
                          onChange={(e) => setExportEndDate(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '0.8rem'
                          }}
                        />
                      </div>
                    </div>
                    
                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem' }}>
                        <input
                          type="checkbox"
                          checked={exportOnlyFlagged}
                          onChange={(e) => setExportOnlyFlagged(e.target.checked)}
                          style={{ transform: 'scale(1.2)' }}
                        />
                        <span style={{ fontWeight: 'bold' }}>Only export flagged conversations (crisis keywords detected)</span>
                      </label>
                    </div>
                    
                    <div style={{ fontSize: '0.7rem', color: '#666', marginBottom: '1rem', fontStyle: 'italic' }}>
                      💡 Leave dates empty to export all time periods. PST timezone will be used for filtering.
                    </div>
                  </div>
                )}
                
                <button
                  onClick={handleExportChatsFiltered}
                  style={{
                    padding: '0.75rem',
                    background: 'var(--color-orange)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    width: '100%',
                    fontWeight: 'bold'
                  }}
                >
                  Export Filtered Chat Logs (CSV)
                </button>
                
                {/* Filter Summary */}
                {(exportStartDate || exportEndDate || exportOnlyFlagged) && (
                  <div style={{ 
                    marginTop: '0.5rem', 
                    padding: '0.5rem', 
                    background: '#e7f3ff', 
                    borderRadius: '4px',
                    fontSize: '0.7rem',
                    color: '#0056b3'
                  }}>
                    <strong>Active Filters:</strong>
                    {exportStartDate && ` From: ${exportStartDate}`}
                    {exportEndDate && ` To: ${exportEndDate}`}
                    {exportOnlyFlagged && ` | Flagged conversations only`}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Document Upload Panel */}
          <div className="admin-panel" style={{
            background: 'white',
            padding: '1.5rem',
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
          }}>
            <h3>📄 Document Management</h3>
            <p>Upload files to pending folder, convert to JSON, and reindex the entire knowledge base</p>
            
            {/* Step 1: Upload File to pending */}
            <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
              <h4 style={{ margin: '0 0 1rem 0', color: 'var(--color-purple)' }}>Step 1: Upload File to Pending</h4>
              <p style={{ fontSize: '0.8rem', color: '#666', margin: '0 0 1rem 0' }}>
                Files are uploaded to backend/data/raw/pending/ for processing
              </p>
              <input
                type="file"
                accept=".pdf,.docx,.html,.htm,.xhtml,.txt,.xls,.xlsx,.csv"
                onChange={handleFileUpload}
                disabled={documentsLoading}
                style={{
                  padding: '0.5rem',
                  border: '2px dashed var(--color-purple)',
                  borderRadius: '6px',
                  background: '#fff',
                  width: '100%'
                }}
              />
              {uploadedFiles.length > 0 && (
                <div style={{ marginTop: '0.5rem' }}>
                  <div style={{ fontSize: '0.9rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>Files in pending folder ({uploadedFiles.length}):</div>
                  <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {uploadedFiles.map((file, index) => (
                      <div key={index} style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        padding: '0.5rem',
                        background: '#e3f2fd',
                        borderRadius: '4px',
                        marginBottom: '0.25rem'
                      }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>{file.filename}</div>
                          <div style={{ fontSize: '0.7rem', color: '#666' }}>
                            {(file.size / 1024).toFixed(1)} KB • {file.extension} • {new Date(file.modified * 1000).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Step 2: Convert All to JSON */}
            <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
              <h4 style={{ margin: '0 0 1rem 0', color: 'var(--color-green)' }}>Step 2: Convert Pending Files to JSON</h4>
              <p style={{ fontSize: '0.8rem', color: '#666', margin: '0 0 1rem 0' }}>
                This will process all files in pending folder and convert them to JSON format in backend/data/processed/. Original files will be moved to backend/data/raw/.
              </p>
              <button
                onClick={handleConvertToJson}
                disabled={converting || uploadedFiles.length === 0}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: converting ? '#ccc' : 'var(--color-green)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: converting || uploadedFiles.length === 0 ? 'not-allowed' : 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: 'bold'
                }}
              >
                {converting ? '🔄 Converting...' : `🔄 Convert Pending Files (${uploadedFiles.length})`}
              </button>
              
              {convertedFiles.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <div style={{ fontSize: '0.9rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>JSON Files in backend/data/processed/ ({convertedFiles.length}):</div>
                  <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                    {convertedFiles.map((file, index) => (
                      <div key={index} style={{ 
                        padding: '0.5rem',
                        background: '#e8f5e8',
                        borderRadius: '4px',
                        marginBottom: '0.25rem'
                      }}>
                        <div style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>{file.filename}</div>
                        <div style={{ fontSize: '0.7rem', color: '#666' }}>
                          {(file.size / 1024).toFixed(1)} KB • {new Date(file.modified * 1000).toLocaleDateString()}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Step 3: Reindex Vector Database */}
            <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
              <h4 style={{ margin: '0 0 1rem 0', color: 'var(--color-orange)' }}>Step 3: Reindex Vector Database</h4>
              <p style={{ fontSize: '0.8rem', color: '#666', margin: '0 0 1rem 0' }}>
                This will rebuild the entire vector database using all JSON files in backend/data/processed/ and store in backend/data/vectors/
              </p>
              
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '1rem' }}>
                <button
                  onClick={handleReindex}
                  disabled={reindexing || convertedFiles.length === 0}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: reindexing ? '#ccc' : 'var(--color-orange)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: reindexing || convertedFiles.length === 0 ? 'not-allowed' : 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: 'bold'
                  }}
                >
                  {reindexing ? '🔄 Reindexing...' : `🚀 Reindex All (${convertedFiles.length} files)`}
                </button>
                
                <button
                  onClick={async () => {
                    if (window.confirm('⚠️ This will clear all processed JSON files and vector database. Continue?')) {
                      try {
                        const token = localStorage.getItem('adminToken');
                        const response = await fetch('/api/admin/clear-documents', {
                          method: 'DELETE',
                          headers: { 'Authorization': `Bearer ${token}` }
                        });
                        if (response.ok) {
                          alert('✅ All processed files and vector database cleared');
                          fetchRawFiles();
                          fetchProcessedFiles();
                        } else {
                          alert('❌ Failed to clear documents');
                        }
                      } catch (error) {
                        alert('❌ Error clearing documents');
                      }
                    }
                  }}
                  style={{
                    padding: '0.5rem 1rem',
                    background: 'var(--color-red)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.8rem'
                  }}
                >
                  🗑️ Clear All
                </button>
              </div>

              {convertedFiles.length === 0 ? (
                <div style={{ color: '#666', fontSize: '0.8rem', fontStyle: 'italic' }}>
                  No JSON files available for indexing. Complete Step 2 first.
                </div>
              ) : (
                <div style={{ fontSize: '0.8rem', color: '#666' }}>
                  Ready to index {convertedFiles.length} JSON files
                </div>
              )}
            </div>

            
            {documentsMessage && (
              <div style={{ 
                fontSize: '0.8rem', 
                padding: '0.5rem',
                borderRadius: '4px',
                marginTop: '0.5rem',
                background: documentsMessage.includes('✅') ? '#d4edda' : '#f8d7da',
                color: documentsMessage.includes('✅') ? '#155724' : '#721c24',
                border: `1px solid ${documentsMessage.includes('✅') ? '#c3e6cb' : '#f5c6cb'}`
              }}>
                {documentsMessage}
              </div>
            )}
          </div>

          {/* Logo Management Panel */}
          <div className="admin-panel" style={{
            background: 'white',
            padding: '1.5rem',
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
          }}>
            <h3>📷 Logo Management</h3>
            <p>Upload a custom logo to replace the default robot emoji</p>
            
            <div style={{ textAlign: 'center', padding: '1rem', background: '#f9f9f9', borderRadius: '6px' }}>
              <p style={{ color: '#666', fontSize: '0.9rem', margin: '0 0 0.5rem 0' }}>Current Logo:</p>
              <Logo size="3rem" />
            </div>
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                Upload New Logo:
              </label>
              <input
                type="file"
                accept=".png,.jpg,.jpeg,.gif,.webp"
                onChange={handleLogoUpload}
                style={{
                  padding: '0.5rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  width: '100%'
                }}
              />
              <small style={{ color: '#666', fontSize: '0.8rem' }}>
                Supported formats: PNG, JPG, GIF, WebP (max 5MB)
              </small>
            </div>
          </div>

          {/* Crisis Keywords Management Panel */}
          <div className="admin-panel" style={{
            background: 'white',
            padding: '1.5rem',
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
          }}>
            <h3>🚨 Crisis Keywords Management</h3>
            <p>Manage crisis detection keywords and triggers</p>
            
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
              <button
                onClick={() => setShowCrisisEditor(!showCrisisEditor)}
                style={{
                  flex: 1,
                  padding: '0.75rem 1.5rem',
                  background: showCrisisEditor ? 'var(--color-red)' : 'var(--color-dark-green)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                {showCrisisEditor ? 'Close Editor' : 'Edit Keywords'}
              </button>
              <button
                onClick={() => setShowTriggers(!showTriggers)}
                style={{
                  flex: 1,
                  padding: '0.75rem 1.5rem',
                  background: showTriggers ? 'var(--color-red)' : 'var(--color-orange)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                {showTriggers ? 'Hide Triggers' : `View Triggers (${crisisTriggers.filter(t => !t.is_reviewed).length})`}
              </button>
            </div>

            {showCrisisEditor && (
              <div style={{ marginTop: '1rem' }}>
                <div style={{ marginBottom: '1rem' }}>
                  <h4 style={{ margin: '0 0 0.5rem 0', color: 'var(--color-red)' }}>
                    Crisis Line Cue Words ({crisisKeywords.crisis_line_cue_words.length})
                  </h4>
                  <p style={{ fontSize: '0.8rem', color: '#666', margin: '0 0 1rem 0' }}>
                    All keywords have equal priority and trigger crisis popup
                  </p>
                  <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
                    <input
                      type="text"
                      value={newKeyword}
                      onChange={(e) => setNewKeyword(e.target.value)}
                      placeholder="Enter new crisis keyword..."
                      style={{ flex: 1, padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
                      onKeyPress={(e) => e.key === 'Enter' && addCrisisKeyword()}
                    />
                    <button
                      onClick={addCrisisKeyword}
                      style={{
                        padding: '0.5rem 1rem',
                        background: 'var(--color-red)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Add
                    </button>
                  </div>
                </div>

                {/* Keywords Display */}
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ 
                    maxHeight: '300px', 
                    overflowY: 'auto',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    padding: '0.5rem'
                  }}>
                    {crisisKeywords.crisis_line_cue_words.length === 0 ? (
                      <p style={{ color: '#666', margin: 0, fontSize: '0.9rem' }}>No crisis keywords</p>
                    ) : (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                        {crisisKeywords.crisis_line_cue_words.map((keyword, index) => (
                          <span
                            key={index}
                            style={{
                              background: '#ffe6e6',
                              border: '1px solid var(--color-red)',
                              padding: '0.25rem 0.5rem',
                              borderRadius: '12px',
                              fontSize: '0.8rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem'
                            }}
                          >
                            {keyword}
                            <button
                              onClick={() => removeCrisisKeyword(keyword)}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: 'var(--color-red)',
                                cursor: 'pointer',
                                fontSize: '0.8rem',
                                padding: '0',
                                width: '14px',
                                height: '14px',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <button
                  onClick={saveCrisisKeywords}
                  style={{
                    padding: '0.75rem',
                    background: 'var(--color-purple)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    width: '100%',
                    marginTop: '1rem'
                  }}
                >
                  Save Keywords
                </button>
              </div>
            )}

            {showTriggers && (
              <div style={{ marginTop: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h4 style={{ margin: 0 }}>Recent Crisis Triggers</h4>
                  {crisisTriggers.filter(t => !t.is_reviewed).length > 0 && (
                    <button
                      onClick={markAllTriggersReviewed}
                      style={{
                        padding: '0.5rem 1rem',
                        background: 'var(--color-purple)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.8rem'
                      }}
                    >
                      ✅ Mark All Reviewed ({crisisTriggers.filter(t => !t.is_reviewed).length})
                    </button>
                  )}
                </div>
                {crisisTriggers.length === 0 ? (
                  <p style={{ color: '#666', fontSize: '0.9rem' }}>No crisis triggers found</p>
                ) : (
                  <div style={{ 
                    maxHeight: '400px', 
                    overflowY: 'auto',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    padding: '0.5rem'
                  }}>
                    {crisisTriggers.map((trigger, index) => (
                      <div key={trigger.id} style={{ 
                        background: trigger.is_reviewed ? '#f9f9f9' : '#fff3cd',
                        border: `1px solid ${trigger.is_reviewed ? '#ddd' : '#ffeaa7'}`,
                        borderRadius: '6px',
                        padding: '0.75rem',
                        marginBottom: '0.5rem'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                            <span style={{
                              background: 'var(--color-red)',
                              color: 'white',
                              padding: '0.2rem 0.5rem',
                              borderRadius: '12px',
                              fontSize: '0.7rem',
                              fontWeight: 'bold'
                            }}>
                              CRISIS
                            </span>
                            <small style={{ color: '#666' }}>
                              {toPSTTime(trigger.timestamp)}
                            </small>
                          </div>
                          {!trigger.is_reviewed && (
                            <button
                              onClick={() => markTriggerReviewed(trigger.id)}
                              style={{
                                background: 'var(--color-green)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '0.25rem 0.5rem',
                                fontSize: '0.7rem',
                                cursor: 'pointer'
                              }}
                            >
                              Mark Reviewed
                            </button>
                          )}
                        </div>
                        
                        <div style={{ marginBottom: '0.5rem' }}>
                          <strong style={{ fontSize: '0.9rem' }}>Triggered Keywords:</strong>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', marginTop: '0.25rem' }}>
                            {trigger.flagged_keywords.map((keyword, i) => (
                              <span key={i} style={{
                                background: 'var(--color-red)',
                                color: 'white',
                                padding: '0.15rem 0.4rem',
                                borderRadius: '8px',
                                fontSize: '0.7rem'
                              }}>
                                {keyword}
                              </span>
                            ))}
                          </div>
                        </div>
                        
                        <div style={{ fontSize: '0.8rem' }}>
                          <strong>User Question:</strong>
                          <div style={{ 
                            background: '#f8f9fa', 
                            padding: '0.5rem', 
                            borderRadius: '4px', 
                            marginTop: '0.25rem',
                            fontStyle: 'italic'
                          }}>
                            "{trigger.user_question}"
                          </div>
                        </div>
                        
                        <div style={{ fontSize: '0.7rem', color: '#666', marginTop: '0.5rem' }}>
                          Session: {trigger.session_id} | IP: {trigger.user_ip}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

        </div>

        {/* System Operations Panel - Moved to Bottom and Centered */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: '3rem',
          marginBottom: '2rem'
        }}>
          <div style={{
            background: 'white',
            padding: '2rem',
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            textAlign: 'center',
            maxWidth: '400px',
            width: '100%',
            border: '2px solid var(--color-red)'
          }}>
            <h3 style={{ 
              margin: '0 0 1rem 0', 
              color: 'var(--color-red)',
              fontWeight: 'bold',
              fontSize: '1.2rem'
            }}>
              ⚙️ System Operations
            </h3>
            <p style={{ 
              color: 'var(--color-red)', 
              margin: '0 0 1.5rem 0',
              fontWeight: 'bold',
              fontSize: '0.9rem'
            }}>
              Dangerous operations - use with caution
            </p>
            
            <button
              onClick={handleClearChats}
              style={{
                padding: '0.75rem 1.5rem',
                background: 'var(--color-red)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                width: '100%',
                fontWeight: 'bold',
                fontSize: '1rem'
              }}
            >
              ⚠️ Clear All Chat History
            </button>
          </div>
        </div>

        {message && (
          <div style={{
            marginTop: '1rem',
            padding: '1rem',
            background: message.includes('✅') ? '#d4edda' : '#f8d7da',
            color: message.includes('✅') ? '#155724' : '#721c24',
            border: `1px solid ${message.includes('✅') ? '#c3e6cb' : '#f5c6cb'}`,
            borderRadius: '8px'
          }}>
            {message}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard; 